// Unified camera rig -- shared by mouse and WAMP (SpaceMouse) driving.
import * as THREE from 'three';
import { clamp } from './math';
import { camera, getModelRadius, pivot, requestRender, updateCameraClipping } from './viewer';

const _forward = new THREE.Vector3();
const _offset = new THREE.Vector3();
const _qBefore = new THREE.Quaternion();
const _qDeltaWorld = new THREE.Quaternion();

export function cameraDistance(): number {
  return Math.max(camera.position.distanceTo(pivot), 1e-6);
}

/**
 * Orbit around the shared pivot without forcing the camera to look at it.
 * Rotating the camera↔pivot offset with the same orientation delta avoids the
 * old look-ray snap (which jumped after SpaceMouse and needed pivot hacks).
 */
export function orbitLocal(pitchRad: number, yawRad: number, rollRad: number): void {
  _qBefore.copy(camera.quaternion);
  _offset.subVectors(camera.position, pivot);

  if (pitchRad) camera.rotateX(pitchRad);
  if (yawRad) camera.rotateY(yawRad);
  if (rollRad) camera.rotateZ(rollRad);

  // World-space rotation that the camera just applied (Q_after * Q_before^-1).
  _qDeltaWorld.copy(camera.quaternion).multiply(_qBefore.invert());
  _offset.applyQuaternion(_qDeltaWorld);
  camera.position.copy(pivot).add(_offset);

  updateCameraClipping();
  requestRender();
}

export function panLocal(dxUnits: number, dyUnits: number): void {
  const right = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);
  const up = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion);
  const offset = new THREE.Vector3()
    .addScaledVector(right, -dxUnits)
    .addScaledVector(up, dyUnits);
  camera.position.add(offset);
  pivot.add(offset);
  updateCameraClipping();
  requestRender();
}

/** factor > 0 moves closer, < 0 moves away — along the camera↔pivot axis. */
export function dollyLocal(factor: number): void {
  dollyTowardPivot(factor);
}

/**
 * Zoom along the camera↔pivot axis without changing orientation.
 * Safe to use while SpaceMouse is driving (no look-ray snaps).
 */
export function dollyTowardPivot(factor: number): void {
  const modelRadius = getModelRadius();
  let dist = cameraDistance() * (1 - factor);
  dist = clamp(dist, modelRadius * 0.02, modelRadius * 80);
  _offset.subVectors(camera.position, pivot);
  if (_offset.lengthSq() < 1e-12) {
    _forward.set(0, 0, -1).applyQuaternion(camera.quaternion);
    camera.position.copy(pivot).addScaledVector(_forward, -dist);
  } else {
    _offset.setLength(dist);
    camera.position.copy(pivot).add(_offset);
  }
  updateCameraClipping();
  requestRender();
}
